import React, { useMemo } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import ReportIcon from "@mui/icons-material/Summarize";
import { useTranslate } from "ra-core";
import { useQuery } from "react-query";
import JSZip from "jszip";

import { downloadData } from "lib/cornerstone/helpers/fileManipulation";
import useViewerParams from "shared/hooks/useViewerParams";
import { ENTRYPOINT } from "config/api";
import { transformPath } from "lib/cornerstone/helpers/niftiDiscovery";

interface ReportProps {
  study_id: string;
}

export const pathologyChoices = [
  "GEN",
  "MCI",
  "MS",
  "EP",
  "PD",
  "CMB",
  "ARIAH",
  "CUSTOM",
];

interface ReportItem {
  id: string;
  name: string;
  filename: string;
  getBlob: () => Promise<Blob>;
}

function Report({ study_id }: ReportProps) {
  const t = useTranslate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { patient_id, acquisition } = useViewerParams();

  const { data: diskPdfs } = useQuery(
    ["disk_pdfs", patient_id, acquisition],
    async () => {
      const diskResp = await fetch(
        `${ENTRYPOINT}/patients/${patient_id}/disk_items/${acquisition}`,
        { credentials: "include" },
      );
      if (!diskResp.ok) return [];
      const diskJ = await diskResp.json();
      const paths: string[] = Array.isArray(diskJ)
        ? diskJ
        : diskJ.items ?? diskJ.disk_items ?? diskJ.files ?? [];

      return paths.filter((p) => p.toLowerCase().endsWith(".pdf"));
    },
    { enabled: !!patient_id && !!acquisition },
  );

  const allReports: ReportItem[] = useMemo(() => {
    return (diskPdfs || []).map((path) => {
      const filename = path.split("/").pop() || "report.pdf";

      return {
        id: path,
        name: getReportNameFromFilename(t, filename),
        filename,
        getBlob: async () => {
          const res = await fetch(
            `${ENTRYPOINT}${transformPath ? transformPath(path) : path}`,
            {
              credentials: "include",
            },
          );
          return await res.blob();
        },
      };
    });
  }, [diskPdfs, t]);

  if (!allReports.length) {
    return null;
  }

  async function showReport(report: ReportItem, isDownload?: boolean) {
    const blob = await report.getBlob();
    viewOrDownloadPDF(blob, isDownload ? report.filename : undefined);
  }

  async function downloadZip(reports: ReportItem[]) {
    const zip = new JSZip();
    const blobs = await Promise.all(reports.map((r) => r.getBlob()));
    blobs.forEach((blob, i) => {
      zip.file(reports[i].filename, blob);
    });
    const zipBlob = await zip.generateAsync({ type: "blob" });
    downloadData(zipBlob, "pdf_reports.zip");
  }

  return (
    <div>
      <Button
        startIcon={<ReportIcon />}
        sx={{ verticalAlign: "bottom", marginBottom: "1em", marginLeft: "2em" }}
        onClick={handleClick}
        variant="contained"
      >
        {t("report.name")}
      </Button>
      <Menu
        id="basic-menu"
        data-testid="report-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {allReports.map((report) => (
          <MenuItem
            key={report.id}
            onClick={() => {
              handleClose();
              showReport(report);
            }}
          >
            {report.name}
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            handleClose();
            Promise.all(allReports.map((report) => showReport(report, true)));
          }}
        >
          {t("report.download")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            downloadZip(allReports);
          }}
        >
          {t("report.download.zip")}
        </MenuItem>
      </Menu>
    </div>
  );
}

// -------------------------------------------------------------------------
// follwing pathologyChoices
// -------------------------------------------------------------------------

export function getReportNameFromFilename(t: Function, filename: string) {
  const fileLower = filename.toLowerCase();

  const pathology = pathologyChoices
    .map((p) => p.toLowerCase())
    .find((p) => fileLower.includes(`_${p}_`) || fileLower.includes(`_${p}.`));

  const options = ["syn", "qc"].filter((o) => fileLower.includes(`_${o}`));

  if (!pathology && options.length === 0) {
    return `${t("report.name")} (${filename})`;
  }

  return `${t("report.name")} ${[pathology, ...options]
    .filter(Boolean)
    .map((attr) => t("report.type." + attr))
    .join(" ")}`;
}

function viewOrDownloadPDF(blob: Blob, fileName?: string) {
  if (fileName) {
    downloadData(blob, fileName);
  } else {
    const data = window.URL.createObjectURL(blob);
    window.open(data);
  }
}

export default Report;
